/**
 * Convert marketplace amounts into a display currency with the daily
 * European Central Bank euro rates from Frankfurter.
 * Quotes stay in the currency they were cached in.
 */

const ECB_RATES_URL = 'https://api.frankfurter.dev/v2/providers/ecb/rates?base=EUR';
const CACHE_KEY = 'riftbound.ecbRates.v1';
const MAX_AGE_MS = 12 * 60 * 60 * 1000;

export type EurRateBook = {
  /** ECB reference date, YYYY-MM-DD. */
  date: string;
  /** Units of each currency per 1 euro. EUR is 1. */
  rates: Record<string, number>;
};

type CachedBook = { book: EurRateBook; fetchedAt: number };

let memory: CachedBook | null = null;
let inflight: Promise<EurRateBook | null> | null = null;

export function moneyFractionDigits(currency: string): number {
  const code = currency.trim().toUpperCase() || 'EUR';
  try {
    return new Intl.NumberFormat('en', {
      style: 'currency',
      currency: code,
    }).resolvedOptions().maximumFractionDigits ?? 2;
  } catch {
    return 2;
  }
}

export function roundMoney(amount: number, currency: string): number {
  const digits = moneyFractionDigits(currency);
  const factor = 10 ** digits;
  return Math.round((amount + Number.EPSILON) * factor) / factor;
}

/**
 * Convert `amount` from `sourceCurrency` into `displayCurrency`.
 * Rates are units of that currency per 1 euro. Returns null when a rate is missing.
 */
export function convertAmount(
  amount: number,
  sourceCurrency: string,
  displayCurrency: string,
  eurRates: Record<string, number>,
): number | null {
  if (!Number.isFinite(amount)) return null;
  const source = sourceCurrency.trim().toUpperCase() || 'EUR';
  const display = displayCurrency.trim().toUpperCase() || 'EUR';
  if (source === display) return amount;
  const sourcePerEur = source === 'EUR' ? 1 : eurRates[source];
  const displayPerEur = display === 'EUR' ? 1 : eurRates[display];
  if (sourcePerEur == null || displayPerEur == null) return null;
  if (!Number.isFinite(sourcePerEur) || sourcePerEur <= 0) return null;
  if (!Number.isFinite(displayPerEur) || displayPerEur <= 0) return null;
  return roundMoney((amount / sourcePerEur) * displayPerEur, display);
}

export function formatRateDate(isoDate: string): string {
  const [year, month, day] = isoDate.split('-').map(Number);
  if (!year || !month || !day) return isoDate;
  return new Intl.DateTimeFormat('en', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(year, month - 1, day)));
}

export function conversionNote(
  sourceCurrency: string,
  displayCurrency: string,
  rateDate: string,
): string {
  const source = sourceCurrency.trim().toUpperCase() || 'EUR';
  const display = displayCurrency.trim().toUpperCase() || 'EUR';
  return `Converted from ${source} to ${display} at the ${formatRateDate(rateDate)} European Central Bank rate.`;
}

function isFresh(entry: CachedBook | null, now: number): entry is CachedBook {
  return entry != null && now - entry.fetchedAt < MAX_AGE_MS && Boolean(entry.book?.date);
}

function readStorage(now: number): CachedBook | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedBook;
    if (!isFresh(parsed, now) || !parsed.book.rates) return null;
    return parsed;
  } catch {
    return null;
  }
}

function parseEcbRows(data: unknown): EurRateBook | null {
  if (!Array.isArray(data)) return null;
  const rates: Record<string, number> = { EUR: 1 };
  let date = '';
  for (const row of data) {
    if (!row || typeof row !== 'object') continue;
    const quote = (row as { quote?: unknown }).quote;
    const rate = (row as { rate?: unknown }).rate;
    const rowDate = (row as { date?: unknown }).date;
    if (typeof quote !== 'string' || typeof rate !== 'number' || !Number.isFinite(rate) || rate <= 0) {
      continue;
    }
    rates[quote.trim().toUpperCase()] = rate;
    if (typeof rowDate === 'string' && rowDate > date) date = rowDate;
  }
  if (!date || Object.keys(rates).length < 2) return null;
  return { date, rates };
}

export function peekEurRates(): EurRateBook | null {
  const now = Date.now();
  if (isFresh(memory, now)) return memory.book;
  const stored = readStorage(now);
  if (!stored) return null;
  memory = stored;
  return stored.book;
}

export async function loadEurRates(): Promise<EurRateBook | null> {
  const cached = peekEurRates();
  if (cached) return cached;
  if (!inflight) {
    inflight = fetch(ECB_RATES_URL)
      .then(async (res) => {
        if (!res.ok) return null;
        const book = parseEcbRows(await res.json());
        if (!book) return null;
        memory = { book, fetchedAt: Date.now() };
        try {
          localStorage.setItem(CACHE_KEY, JSON.stringify(memory));
        } catch {
          /* private mode and quota errors leave the in-memory copy */
        }
        return book;
      })
      .catch(() => null)
      .finally(() => {
        inflight = null;
      });
  }
  return inflight;
}

export function resetEurRateCacheForTests(): void {
  memory = null;
  inflight = null;
  try {
    localStorage.removeItem(CACHE_KEY);
  } catch {
    /* ignore */
  }
}
